export const decideFontColor = (bgColor: string): string => {
  const hex = bgColor.replace('#', '');

  // Parse the hex color to RGB values
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Calculate perceived brightness
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? 'black' : 'white';
}

export const adjustColor = (color: string, amount: number) => {
  return '#' + color.replace(/^#/, '').replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2));
}

export function lightenColor(color: string, amount: number): string {
  let usePound = false;
  if (color[0] === '#') {
    color = color.slice(1);
    usePound = true;
  }
  const num = parseInt(color, 16);
  let r = (num >> 16) + amount;
  if (r > 255) r = 255;
  else if (r < 0) r = 0;
  let b = ((num >> 8) & 0x00FF) + amount;
  if (b > 255) b = 255;
  else if (b < 0) b = 0;
  let g = (num & 0x0000FF) + amount;
  if (g > 255) g = 255;
  else if (g < 0) g = 0;
  return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
}

const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result)
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    }
  return {r: 0, g: 0, b: 0}
}

const rgbToHex = (r: number, g: number, b: number) => {
  const componentToHex = (c: number) => {
    const hex = c.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }
  return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}


export const simulateOpacity = (color: string, opacity: number) => {
  const asRgb = hexToRgb(color);
  const r = Math.floor(asRgb.r * opacity + 255 * (1 - opacity));
  const g = Math.floor(asRgb.g * opacity + 255 * (1 - opacity));
  const b = Math.floor(asRgb.b * opacity + 255 * (1 - opacity));
  return rgbToHex(r, g, b);
}

// ----- New Methods ----- //
/**
 * Generate darker and lighter versions of a color with intelligent adjustments
 * @param color - Hex color string (e.g., '#ff5733' or 'ff5733')
 * @returns Object with darker and lighter color variants
 */
export function getColorVariants(color: string): { darker: string; lighter: string } {
  const hex = color.replace('#', '');

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Convert to HSL for better color manipulation
  const hsl = rgbToHsl(r, g, b);

  // Calculate darker version (for borders)
  // Darken by reducing lightness, more aggressive for lighter colors
  let darkerLightness: number;
  if (hsl.l > 0.7) {
    // Very light colors: darken significantly
    darkerLightness = hsl.l * 0.4;
  } else if (hsl.l > 0.5) {
    // Medium-light colors: moderate darkening
    darkerLightness = hsl.l * 0.6;
  } else if (hsl.l > 0.2) {
    // Medium-dark colors: subtle darkening
    darkerLightness = hsl.l * 0.7;
  } else {
    // Very dark colors: minimal darkening
    darkerLightness = Math.max(0, hsl.l * 0.8);
  }

  // Slightly increase saturation for darker version to keep it vibrant
  const darkerSaturation = Math.min(1, hsl.s * 1.1);

  // Calculate lighter version (for backgrounds)
  // Move towards white while maintaining a subtle tint
  let lighterLightness: number;
  let lighterSaturation: number;

  if (hsl.l < 0.3) {
    // Very dark colors: lighten a lot and reduce saturation significantly
    lighterLightness = 0.95;
    lighterSaturation = hsl.s * 0.15;
  } else if (hsl.l < 0.5) {
    // Medium-dark colors: lighten significantly
    lighterLightness = 0.93;
    lighterSaturation = hsl.s * 0.2;
  } else if (hsl.l < 0.7) {
    // Medium-light colors: moderate lightening
    lighterLightness = 0.95;
    lighterSaturation = hsl.s * 0.25;
  } else {
    // Already light: just slight adjustment
    lighterLightness = 0.97;
    lighterSaturation = hsl.s * 0.3;
  }

  const darker = hslToRgb(hsl.h, darkerSaturation, darkerLightness);
  const lighter = hslToRgb(hsl.h, lighterSaturation, lighterLightness);

  return { darker, lighter };
}

/**
 * Calculate appropriate text color based on background with APCA algorithm
 * @param bgColor - Background hex color string
 * @returns Hex color string for text
 */
export function getTextColor(bgColor: string): string {
  const hex = bgColor.replace('#', '');

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Convert to HSL
  const hsl = rgbToHsl(r, g, b);

  // Use relative luminance for accurate perceived brightness
  // (HSL lightness doesn't account for how the eye perceives color channels differently)
  const relativeLuminance = getRelativeLuminance(r, g, b);

  // Determine if we need light or dark text
  // Threshold ~0.36 gives good contrast for vibrant colors like pinks/reds
  const needsLightText = relativeLuminance < 0.36;

  if (needsLightText) {
    // Dark background: use white
    return '#ffffff';
  }

  // Light background: create a darker version that maintains color harmony
  let textLightness: number;
  let textSaturation: number;

  if (hsl.l > 0.85) {
    // Very light backgrounds: use quite dark text
    textLightness = 0.2;
    textSaturation = Math.min(0.6, hsl.s * 1.5);
  } else if (hsl.l > 0.7) {
    // Light backgrounds: use dark text
    textLightness = 0.25;
    textSaturation = Math.min(0.5, hsl.s * 1.3);
  } else {
    // Medium backgrounds: moderately dark text
    textLightness = 0.3;
    textSaturation = Math.min(0.4, hsl.s * 1.2);
  }

  // For very low saturation (grays), use pure gray
  if (hsl.s < 0.1) {
    return '#1a1a1a';
  }

  // Use the same hue but darkened and slightly more saturated
  return hslToRgb(hsl.h, textSaturation, textLightness);
}

// sRGB relative luminance per WCAG 2.x
function getRelativeLuminance(r: number, g: number, b: number): number {
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

// Helper functions for HSL conversion
function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (diff !== 0) {
    s = l > 0.5 ? diff / (2 - max - min) : diff / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / diff + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / diff + 2) / 6;
        break;
      case b:
        h = ((r - g) / diff + 4) / 6;
        break;
    }
  }

  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): string {
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (n: number) => Math.round(n * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
